import FarmShareBG from '../../assets/FarmshareBG.jpg';
import { Navbar } from '../../components/Navbar';

import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState, useMemo } from 'react';
import {
  getAllReservations,
  updateReservation,
  clearError,
} from '../../redux/slices/reservationSlice';
import { getSpecificUser } from '../../redux/slices/userSlice';
import { getSingleEquipment } from '../../redux/slices/equipmentsSlice';
import { ReservationFilter } from '../../components/ReservationFilter';

import { ReservationCard } from '../../components/ReservationCard';

import { ReservationModal } from '../../components/ReservationModal';

import { ReservationInfoModal } from '../../components/ReservationInfoModal';

import { LoadingModal } from '../../components/LoadingModal';
import { toast } from 'react-toastify';
import { ReactComponent as DwnldArrow } from '../../assets/download-arrow.svg';
import { CSVLink } from 'react-csv';
import { ProcessReturnModal } from '../../components/ProcessReturnModal';

const ReservationList = () => {
  // Redux state
  const reservations = useSelector((state) => state.reservations.reservations);
  const loading = useSelector((state) => state.reservations.loading);
  const error = useSelector((state) => state.reservations.error);
  const dispatch = useDispatch();

  // Local state
  const [users, setUsers] = useState({});
  const [equipments, setEquipments] = useState({});
  const [currentFilter, setCurrentFilter] = useState('all');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [archiveRes, setArchiveRes] = useState(null);
  const [currentAction, setCurrentAction] = useState('');

  // Modal states
  const [approveModal, setApproveModal] = useState(false);
  const [denyModal, setDenyModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [deleteReservationModal, setDeleteReservationModal] = useState(false);
  const [showReservationInfoModal, setShowReservationInfoModal] =
    useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);

  // Fetch all reservations on component mount
  useEffect(() => {
    dispatch(getAllReservations());
  }, [dispatch]);

  const handleDismissError = () => {
    dispatch(clearError());
  };

  // Filter reservations based on current filter
  const filteredReservations = useMemo(() => {
    if (!reservations.length) return [];

    if (currentFilter === 'all') {
      return reservations.filter((reservation) => !reservation.archived);
    } else if (currentFilter === 'archived') {
      return reservations.filter((reservation) => reservation.archived);
    } else {
      return reservations.filter(
        (reservation) =>
          reservation.reservationStatus === currentFilter &&
          !reservation.archived,
      );
    }
  }, [reservations, currentFilter]);

  // Prepare download data with properly named columns and removed unwanted columns
  const downloadReservations = useMemo(() => {
    if (!reservations.length) return [];

    return reservations.map((reservation) => {
      // Get user and equipment info with fallbacks
      const user = users[reservation.userId];
      const userName = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          'Unknown User'
        : 'Unknown User';

      const equipment = equipments[reservation.equipmentId];
      const equipmentName = equipment?.name || 'Unknown Equipment';

      return {
        'Reservation ID': reservation._id,
        'Renter Name': userName,
        'Equipment Name': equipmentName,
        'Start Date': reservation.startDate,
        'End Date': reservation.endDate,
        Status: reservation.reservationStatus,
        'Rental Quantity': reservation.rentalTotal,
        'Rental Price': reservation.rentalPrice,
        'Delivery Assistance': reservation.deliveryAssistance ? 'YES' : 'NO',
        'Pickup Assistance': reservation.pickupAssistance ? 'YES' : 'NO',
        ...(reservation.returnCondition && {
          'Return Condition': reservation.returnCondition,
        }),
        ...(reservation.returnDate && {
          'Return Date': reservation.returnDate,
        }),
        ...(reservation.returnNotes && {
          ReturnNotes: reservation.returnNotes,
        }),
        ...(reservation.damageNotes && {
          'Damage Notes': reservation.damageNotes,
        }),
        ...(reservation.estimatedDamageCost && {
          'Estimated Damage Cost': reservation.estimatedDamageCost,
        }),
        'Created At': reservation.createdAt,
        'Updated At': reservation.updatedAt,
        Archived: reservation.archived ? 'YES' : 'NO',
      };
    });
  }, [reservations, users, equipments]);

  useEffect(() => {
    if (!reservations.length) return;

    const fetchUsers = async () => {
      const userIds = [...new Set(reservations.map((r) => r.userId))];

      for (const userId of userIds) {
        if (!users[userId]) {
          try {
            const action = await dispatch(getSpecificUser(userId));
            if (getSpecificUser.fulfilled.match(action)) {
              setUsers((prev) => ({
                ...prev,
                [userId]: action.payload,
              }));
            }
          } catch (err) {
            console.error(`Error fetching user with ID ${userId}:`, err);
            setUsers((prev) => ({
              ...prev,
              [userId]: { firstName: '', lastName: '' },
            }));
          }
        }
      }
    };

    const fetchEquipments = async () => {
      const equipIds = [...new Set(reservations.map((r) => r.equipmentId))];

      for (const equipId of equipIds) {
        if (!equipments[equipId]) {
          try {
            const action = await dispatch(getSingleEquipment(equipId));
            if (getSingleEquipment.fulfilled.match(action)) {
              setEquipments((prev) => ({
                ...prev,
                [equipId]: action.payload,
              }));
            }
          } catch (err) {
            console.error(`Error fetching equipment with ID ${equipId}:`, err);
            setEquipments((prev) => ({
              ...prev,
              [equipId]: { name: 'Unknown Equipment' },
            }));
          }
        }
      }
    };

    fetchUsers();
    fetchEquipments();
  }, [dispatch, reservations]);

  const handleArchive = async () => {
    if (!archiveRes) return;

    try {
      setCurrentAction('Archiving reservation...');
      // Close the confirmation modal first
      setDeleteReservationModal(false);

      await dispatch(
        updateReservation({ _id: archiveRes, archived: true }),
      ).unwrap();
      toast.success('Reservation archived successfully');
      setArchiveRes(null);
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
    }
  };

  const handleReturnProcessed = () => {
    dispatch(getAllReservations());
  };

  return (
    <div className="bg-sky-200 min-h-screen flex flex-col">
      <Navbar src={FarmShareBG} pageName="Reservation List" pageText="" />

      {/* Loading Modal */}
      {(loading || error) && (
        <LoadingModal
          message={currentAction || 'Loading reservations...'}
          error={error}
          onClose={handleDismissError}
        />
      )}

      <ReservationFilter
        filterReservations={setCurrentFilter}
        setCurrentFilter={setCurrentFilter}
        currentFilter={currentFilter}
      />

      {approveModal && (
        <ReservationModal
          setApproveModal={setApproveModal}
          setCancelModal={setCancelModal}
          setDenyModal={setDenyModal}
          selectedReservation={selectedReservation}
          mode={'Approve'}
          setSelectedReservation={setSelectedReservation}
          setCurrentAction={setCurrentAction}
        />
      )}

      {denyModal && (
        <ReservationModal
          setApproveModal={setApproveModal}
          setCancelModal={setCancelModal}
          setDenyModal={setDenyModal}
          mode={'Deny'}
          selectedReservation={selectedReservation}
          setSelectedReservation={setSelectedReservation}
          setCurrentAction={setCurrentAction}
        />
      )}

      {cancelModal && (
        <ReservationModal
          setApproveModal={setApproveModal}
          setCancelModal={setCancelModal}
          setDenyModal={setDenyModal}
          mode={'Cancel'}
          selectedReservation={selectedReservation}
          setSelectedReservation={setSelectedReservation}
          setCurrentAction={setCurrentAction}
        />
      )}

      {deleteReservationModal && (
        <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-md shadow-lg w-1/2 max-w-lg">
            <form>
              <label className="block mb-2 text-lg font-bold">
                Are You Sure You Want To{' '}
                <span className="text-red-500">Archive</span> This Reservation?
              </label>
              <div className="flex justify-end">
                <button
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md"
                  type="button"
                  onClick={handleArchive}
                  disabled={loading}
                >
                  {loading ? 'Archiving...' : 'Confirm'}
                </button>
                <button
                  className="ml-4 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"
                  onClick={() => setDeleteReservationModal(false)}
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReservationInfoModal && selectedReservation && (
        <ReservationInfoModal
          setShowReservationInfoModal={setShowReservationInfoModal}
          setSelectedReservation={setSelectedReservation}
          reservation={selectedReservation}
          renterName={
            users[selectedReservation.userId]
              ? `${users[selectedReservation.userId].firstName || ''} ${
                  users[selectedReservation.userId].lastName || ''
                }`.trim() || 'Unknown User'
              : 'Unknown User'
          }
          equipmentName={
            equipments[selectedReservation.equipmentId]?.name ||
            'Unknown Equipment'
          }
        />
      )}

      {showReturnModal && selectedReservation && (
        <ProcessReturnModal
          setShowReturnModal={setShowReturnModal}
          reservation={selectedReservation}
          equipmentName={
            equipments[selectedReservation.equipmentId]?.name ||
            'Unknown Equipment'
          }
          onReturnProcessed={handleReturnProcessed}
        />
      )}

      <div className="w-full py-5 px-[8vw] md:px-16 lg:px-[10vw]">
        {!loading && !error && (
          <div>
            <div className="w-full flex mb-4">
              {reservations.length > 0 ? (
                <CSVLink
                  className="bg-[#2E7A32] mt-1 hover:bg-green-800 hover:border-green-900 border-green-800 border-b-2 border-r-2 text-white font-bold py-2 px-4 rounded-full flex items-center space-x-2 transition duration-300"
                  data={downloadReservations}
                  target="_blank"
                  filename={'Farmshare-reservations.csv'}
                >
                  <DwnldArrow width={20} height={20} />
                  <span>Download All Reservations</span>
                </CSVLink>
              ) : (
                <button
                  className="bg-gray-400 mt-1 text-white font-bold py-2 px-4 rounded-full flex items-center space-x-2 cursor-not-allowed"
                  disabled
                  aria-disabled="true"
                  title="No reservations available to download"
                >
                  <DwnldArrow width={20} height={20} />
                  <span>Download All Reservations</span>
                </button>
              )}
            </div>

            {filteredReservations.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-5 bg-gray-100 rounded-md shadow-md w-full">
                <span className="text-gray-700 text-lg">
                  No reservations in our system right now!
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 gap-6 w-full">
                {filteredReservations.map((reservation) => {
                  const user = users[reservation.userId];
                  const name = user
                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                      'Unknown User'
                    : 'Unknown User';

                  const equip = equipments[reservation.equipmentId];
                  const equipName = equip?.name || 'Unknown Equipment';

                  return (
                    <ReservationCard
                      key={reservation._id}
                      startDate={reservation.startDate}
                      endDate={reservation.endDate}
                      status={reservation.reservationStatus}
                      equipmentName={equipName}
                      renterName={name}
                      setApproveModal={setApproveModal}
                      setCancelModal={setCancelModal}
                      setDenyModal={setDenyModal}
                      setDeleteModal={setDeleteReservationModal}
                      setArchiveRes={setArchiveRes}
                      resId={reservation._id}
                      reservation={reservation}
                      setSelectedReservation={setSelectedReservation}
                      setShowReservationInfoModal={setShowReservationInfoModal}
                      setCurrentAction={setCurrentAction}
                      setShowReturnModal={setShowReturnModal}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { ReservationList };
